import { Badge, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useNotification } from '@/console/features/notifications/hooks/use-notification/use-notification';
import { NotificationsIcon } from '@/shared/assets/icons/notifications-icon';
import { HeaderIconButton } from '@/shared/components/ui/button/header-icon-button/header-icon-button';
import { useAppSyncEvents } from '@/shared/hooks/use-appsync-events/use-app-sync-events';
import { useLoggedInUser } from '@/shared/hooks/use-logged-in-user/use-logged-in-user';
import { useReadTenantQuery } from '@/shared/redux/api/ident/ident-api';
import {
  notificationsApi,
  useUnreadNotificationsCountQuery,
} from '@/shared/redux/api/notifications/notifications-api';
import { snakeToCamelCase } from '@/shared/utils/transform';

export const NotificationsButton = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: tenantData } = useReadTenantQuery();
  const { loggedInUser } = useLoggedInUser();
  const { data, refetch } = useUnreadNotificationsCountQuery(undefined, {
    pollingInterval: 60000,
  });
  const { renderMessage, renderIcon } = useNotification();

  const webSocketChannel = `default/${tenantData?.readTenant.tenantId}/notifications/${loggedInUser?.userId}`;

  const onMessageReceived = (data: any) => {
    const notification = snakeToCamelCase(data?.event);

    if (!notification || !notification.notificationId) {
      return;
    }

    toast.info(<Typography>{renderMessage(notification)}</Typography>, {
      icon: renderIcon(notification, { variant: 'info', size: '1.5rem' }),
      onClick,
    });
    refetch();
    dispatch(notificationsApi.util.invalidateTags(['Notification']));
  };

  useAppSyncEvents({
    webSocketChannel,
    connectOnInit: true,
    onMessageReceived,
  });

  const unreadCount = data?.readAllNotifications.itemCount ?? 0;
  const onClick = () => navigate('/notifications');

  const getTooltipText = () => {
    if (unreadCount === 0) {
      return '';
    }
    return t('notifications:unread_notifications_count', {
      count: unreadCount,
    });
  };

  return (
    <HeaderIconButton onClick={onClick} tooltip={getTooltipText()}>
      <Badge color="primary" variant="dot" invisible={unreadCount === 0}>
        <NotificationsIcon />
      </Badge>
    </HeaderIconButton>
  );
};
